/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { useRetracked } from 'js/lib/retracked';

import { Button, ContextualHelp, Grid, InlineNotification, Typography } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import {
  PortfolioBuilderContext,
  PortfolioBuilderContextProvider,
} from 'bundles/account-profile/components/recruiting-profile-viewer/PortfolioBuilderContextProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ProfileViewerCard from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileViewerCard';
import ProjectCompletionDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/ProjectCompletionDialog';
import ProjectsBody from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/ProjectsBody';
import ProjectsInfoDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/ProjectsInfoDialog';
import { MAX_VISIBLE_PROJECTS } from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/constants';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { useEnableAutoPublishedProjects } from 'bundles/employability-common/feature-flags/hooks/useEnableAutoPublishedProjects';
import PortfolioBuilder from 'bundles/portfolio-projects/components/PortfolioBuilder';
import type { SharedPortfolioProject_UserPortfolioProjectFragment as PortfolioProject } from 'bundles/portfolio-projects/queries/__generated__/SharedPortfolioProject_UserPortfolioProject';

import _t from 'i18n!nls/account-profile';

import ViewProjectsDialog from './ViewProjectsDialog';

const styles = {
  withBottomMargin: (theme: Theme) => css`
    margin-bottom: ${theme.spacing(24)};

    ${theme.breakpoints.down('sm')} {
      margin-bottom: ${theme.spacing(16)};
    }
  `,
  withTopMargin: (theme: Theme) => css`
    ${theme.breakpoints.down('xs')} {
      margin-top: ${theme.spacing(16)};
    }
  `,
  withMarginLeft: (theme: Theme) => css`
    margin-left: ${theme.spacing(24)};
  `,
};

const ProjectsSection = () => {
  const retrackedTrack = useRetracked();
  const enableAutoPublishedProjects = useEnableAutoPublishedProjects();

  const {
    isProfileLoading,
    editable,
    profileState,
    retrackedEventingData,
    updatePortfolioProject: profileContextUpdateProject,
    sharedEventingV3Data,
  } = useContext(ProfileContext);

  const [showViewProjectDialog, setShowViewProjectDialog] = useState(false);

  const {
    isPortfolioBuilderOpen,
    openPortfolioBuilder,
    closePortfolioBuilder,
    updatePortfolioProject: portfolioBuilderContextUpdateProject,
    projectsList,
    selectedProject,
  } = useContext(PortfolioBuilderContext);

  const track = useTracker();
  const infoTooltipVisibilityTrackingRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker(
    'view_button',
    {
      button: {
        name: EVENTS.profileProjectsInformationTooltip,
      },
      ...sharedEventingV3Data,
    }
  );

  useEffect(() => {
    // completedPortfolioProjects or draftPortfolioProjects will only be null if there was an error fetching the data
    if (!isProfileLoading && profileState?.completedPortfolioProjects === null) {
      Sentry.captureMessage('There was an error fetching completed projects', {
        extra: {
          externalId: profileState?.externalId,
        },
      });
    }

    if (!isProfileLoading && !profileState?.draftPortfolioProjects === null) {
      Sentry.captureMessage('There was an error fetching draft projects', {
        extra: {
          externalId: profileState?.externalId,
        },
      });
    }
  }, [
    profileState?.completedPortfolioProjects,
    profileState?.draftPortfolioProjects,
    profileState?.externalId,
    isProfileLoading,
  ]);

  const onProjectUpdate = useCallback(
    (project: PortfolioProject) => {
      profileContextUpdateProject(project);
      portfolioBuilderContextUpdateProject(project);
    },
    [profileContextUpdateProject, portfolioBuilderContextUpdateProject]
  );

  const draftPortfolioProjects = profileState?.draftPortfolioProjects || [];
  const completedPortfolioProjects = profileState?.completedPortfolioProjects || [];
  const numOfCompletedProjects = completedPortfolioProjects.length;
  const totalNumOfProjects = numOfCompletedProjects + draftPortfolioProjects.length;

  const trackingData = {
    // eslint-disable-next-line camelcase
    candidate_profile: retrackedEventingData.candidate_profile,
    program: retrackedEventingData.program,
  };

  // If there are no completed projects, don't render the section for recruiters
  if (!isProfileLoading && numOfCompletedProjects === 0 && !editable) {
    return null;
  }

  const mostRecentlyCompletedCapstone = [...draftPortfolioProjects, ...completedPortfolioProjects]
    .filter((proj) => proj.projectType === 'CAPSTONE')
    .sort((a, b) => b.completed - a.completed)[0];

  const isMostRecentProjectAutoPublished = mostRecentlyCompletedCapstone?.isAutoPublished;
  const handleAddProject = () => {
    openPortfolioBuilder({
      projectsList: draftPortfolioProjects,
      isPublished: false,
    });
  };

  const handleEditProject = () => {
    openPortfolioBuilder({
      project: mostRecentlyCompletedCapstone,
      isPublished: true,
    });
  };

  const closeViewProjectDialog = () => {
    setShowViewProjectDialog(false);
  };

  const handleViewProject = () => {
    setShowViewProjectDialog(true);
  };

  return (
    <ProfileViewerCard>
      <Grid container alignItems="center" justifyContent="space-between" css={styles.withBottomMargin}>
        <ContextualHelp
          helpToggleProps={{
            'aria-label': _t('Information about the projects section'),
          }}
          label={
            <Typography variant="h2semibold" component="h3" data-testid="projects-section-title">
              {_t('Projects')}
            </Typography>
          }
          onChange={() => {
            // I need to use the tracking function from useRetracked because at the time of writing, there's no way to pass a TrackedButton
            // into the <ContextualHelp> component.
            retrackedTrack({
              trackingData,
              trackingName: EVENTS.profileProjectsInformationTooltip,
              action: 'click',
            });

            track('click_button', {
              button: {
                name: EVENTS.profileProjectsInformationTooltip,
              },
              ...sharedEventingV3Data,
            });
          }}
          ref={infoTooltipVisibilityTrackingRef}
        >
          {({ onClose, isExpanded = false }) => <ProjectsInfoDialog isOpen={isExpanded} onClose={onClose} />}
        </ContextualHelp>
        {editable && numOfCompletedProjects !== totalNumOfProjects && numOfCompletedProjects > MAX_VISIBLE_PROJECTS && (
          <Button
            variant="secondary"
            onClick={handleAddProject}
            icon={<AddIcon />}
            iconPosition="before"
            size="small"
            css={styles.withMarginLeft}
          >
            {_t('Add')}
          </Button>
        )}
      </Grid>
      <ProjectsBody
        draftPortfolioProjects={draftPortfolioProjects}
        completedPortfolioProjects={completedPortfolioProjects}
      />
      <PortfolioBuilder
        open={isPortfolioBuilderOpen}
        title={selectedProject ? _t('Edit project') : _t('Add project')}
        canOpenProjectHome
        project={selectedProject}
        projectsList={projectsList}
        onClose={closePortfolioBuilder}
        onProjectUpdate={onProjectUpdate}
      />

      {mostRecentlyCompletedCapstone && editable && (
        <ViewProjectsDialog
          key="viewProjectDialog~mostRecentlyCompletedCapstone"
          isOpen={showViewProjectDialog}
          onClose={closeViewProjectDialog}
          initialCompletedProject={mostRecentlyCompletedCapstone}
          // We don't want the arrows on the view projects dialog to be present when launched from the most recently completed
          // capstone dialog, so we make it appear as though there's only one completed portfolio project.
          completedPortfolioProjects={[mostRecentlyCompletedCapstone]}
        />
      )}

      {enableAutoPublishedProjects && mostRecentlyCompletedCapstone && editable && (
        <ProjectCompletionDialog
          projectTitle={mostRecentlyCompletedCapstone.title}
          projectId={mostRecentlyCompletedCapstone.id}
          isAutoPublished={isMostRecentProjectAutoPublished}
          onAddProjectClick={handleAddProject}
          onEditProjectClick={handleEditProject}
          onViewProjectClick={handleViewProject}
          key={mostRecentlyCompletedCapstone.id}
        />
      )}
    </ProfileViewerCard>
  );
};

const ErrorBoundProjectsSection = () => (
  <ErrorBoundaryWithTagging
    componentName="ProjectsSection"
    errorComponent={
      <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
        {_t('There has been an error in the projects section. Our development team will be alerted.')}
      </InlineNotification>
    }
  >
    <PortfolioBuilderContextProvider>
      <ProjectsSection />
    </PortfolioBuilderContextProvider>
  </ErrorBoundaryWithTagging>
);

export default ErrorBoundProjectsSection;
